import { shade, tint } from 'polished';
import React, { useCallback, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { Icon } from '../concepts/resources/Icon';
import { Colors } from './Colors';
import { Input } from './Input';
import { usePrompt } from './Prompt';
import { Slider } from './Slider';
import { toRem } from './Utils';

export const ColorList = [
    '#9c27b0',
    '#673ab7',
    '#3f51b5',
    '#2196f3',
    '#03a9f4',
    '#00bcd4',
    '#009688',
    '#4caf50',
    '#8bc34a',
    '#cddc39',
    '#ffeb3b',
    '#ffc107',
    '#ff9800',
    '#ff5722',
    '#f44336',
    '#e91e63',
    '#795548',
    '#607d8b',
    '#000000',
    '#404040',
    '#808080',
    '#BFBFBF',
    '#DFDFDF',
    '#ffffff',
];

const shadeOrTint = (color: string, by: number) => {
    if (by < 1) return tint(1 - by, color);
    if (by > 1) return shade(by - 1, color);
    return color;
};

const colorSeeds = [.1, .2, .4, .6, .8, 1, 1.1, 1.2, 1.4];
const colorSeedOrigin = colorSeeds.findIndex(x => x === 1);

export const ColorGroups = colorSeeds
    .map(x => ColorList.map(y => Colors.normalize(shadeOrTint(y, x))));

export const ColorGroup = (() => {

    // Index colors with priorty nearest to 1
    const index = new Map<string, number>();
    const dist = (gi: number) => Math.abs(gi - colorSeedOrigin);
    ColorGroups.forEach((cg, gi) => {
        for (const c of cg) {
            if (index.has(c)) {
                if (dist(gi) > dist(index.get(c))) continue;
                if (gi > colorSeedOrigin && gi > index.get(c)) continue;
            }
            index.set(c, gi);
        }
    });

    return Object.freeze({

        default: ColorGroups[4],

        find(color: string) {
            color = Colors.normalize(color);
            return index.get(color) ?? null;
        },

        findPeer(color: string, groupIndex: number, thisIndex?: number) {
            if (!color) return color;
            const thisGroup = ColorGroups[thisIndex ?? ColorGroup.find(color)];
            if (!thisGroup) return color;
            const colorIndex = thisGroup.indexOf(color);
            const prefGroup = ColorGroups[groupIndex];
            return (prefGroup ?? [])[colorIndex] ?? color;
        }
    });

})();

const Frame = styled.div<{ width?: any }>`
    width: ${p => toRem(p.width ?? 15.6)};
    padding: 0.25rem;
    background: white;
`;

const CheckerBoard = styled.div<{ width?: any }>`
    display: flex;
    flex-wrap: wrap;
`;

const CheckerItem = styled.div<{ active?: boolean }>`
    position: relative;
    flex: 0 0 auto;
    width: 2.5rem;
    height: 2.5rem;
`;

const CheckerColor = styled.div<{ active?: boolean }>`
    position: absolute;
    inset: .1875rem;
    border: solid .0625rem rgba(0,0,0,.05);
    border-radius: .25rem;
    cursor: pointer;
    
    ${p => !p.active ? '' : css`
        inset: .0625rem;
        border: solid .125rem rgba(0,0,0,.15);
        border-radius: .375rem;
    `}

    &:hover {
        inset: .0625rem;
        border: solid .125rem rgba(0,0,0,.15);
        border-radius: .375rem;
    }
    &:active {
        inset: .0625rem;
        border: solid .125rem rgba(0,0,0,.3);
        border-radius: .375rem;
    }
`;

const CheckerStrike = styled.div<{ side: string }>`
    position: absolute;
    left: 50%;
    top: 50%;
    width: 100%;
    background: rgb(139 0 0 / 80%);
    -webkit-transform: translateX(-1px) rotate(45deg);
    -ms-transform: translateX(-1px) rotate(45deg);
    transform: translate(-50%, -50%) rotate(-45deg);
    height: 0.1875rem;
    border-radius: 100px;
    pointer-events: none;
`;

const InputFrame = styled.div<{ width: any }>`
    width: ${p => toRem(p.width)};
    margin: .1875rem;
`;

const SliderFrame = styled.div`
    display: flex;
    align-items: center;
    padding: .5rem .25rem;
    gap: .25rem;
`;

const SliderIcon = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    color: ${Colors.primary};
`;

const SliderInner = styled.div`
    flex: 1 1 auto;
`;

export type ColorSelectorCallback = (color: string | null, complete: boolean) => void;

export type ColorSelectorOptions = {
    allowNull?: boolean,
    autoSelect?: boolean,
    groups?: boolean,
};

export type ColorSelectorProps = ColorSelectorOptions & {
    color?: string,
    width?: number | string,
    onSelect: ColorSelectorCallback,
};

export const ColorSelector = React.memo<ColorSelectorProps>(({ color, allowNull, autoSelect, groups, width, onSelect }) => {
    color = Colors.normalize(color);

    const [group, setGroup] = useState(ColorGroup.find(color) ?? 3);
    const [original, setOriginal] = useState(color);
    const [hex, setHex] = useState(color ?? '');
    const list = useMemo(() => ColorGroups[group] ?? ColorList, [group]);

    const onCheck = (value: string | null, complete?: boolean) => {
        const normalized = Colors.normalize(value);
        setHex(removeHash(normalized));
        if (normalized || allowNull) {
            if (complete) setOriginal(normalized);
            onSelect(normalized, complete);
        }
    };

    const onReset = () => {
        onCheck(original, false);
    };

    const onInput = useCallback((value: string) => {
        setHex(removeHash(value));
        if (value === '' && allowNull) {
            onSelect(null, false);
        }
        else {
            value = Colors.normalize(value) ?? color;
            if (value) onSelect(value, false);
        }
    }, [allowNull, onSelect]);

    const onBrightness = useCallback((groupIndex: number) => {
        setGroup(groupIndex);
        const peer = ColorGroup.findPeer(color, groupIndex, group);
        if (peer) {
            onSelect(peer, false);
        }
    }, [color, group, onSelect]);

    const onFinish = useCallback(() => onSelect(color, true), [color, onSelect]);

    const promptRef = usePrompt({
        onAction: onFinish,
        ignoreMouse: true,
    });

    return <Frame ref={promptRef} width={width}>
        <CheckerBoard>
            {list.map(x =>
                <CheckerItem key={x}
                    active={color === x}
                    onClick={() => onCheck(x, true)}
                    onMouseEnter={autoSelect ? () => onCheck(x) : null}
                    onMouseLeave={autoSelect ? onReset : null}>
                    <CheckerColor active={color === x} style={{ background: x }} />
                </CheckerItem>
            )}
            {allowNull &&
                <CheckerItem
                    active={color === null}
                    onClick={() => onCheck(null, true)}
                    onMouseEnter={autoSelect ? () => onCheck(null) : null}
                    onMouseLeave={autoSelect ? onReset : null}>
                    <CheckerColor active={color === null} />
                    <CheckerStrike side="left" />
                </CheckerItem>
            }
            <InputFrame width={allowNull ? 12.22 : '100%'}>
                <Input compact value={removeHash(hex) ?? ''}
                    onChange={x => onInput(x)}
                    before={() => <span>#</span>} />
            </InputFrame>
        </CheckerBoard>
        {groups &&
            <SliderFrame>
                <SliderIcon>
                    <Icon size="1" src="deqo://icons/hero-mini/sun" />
                </SliderIcon>
                <SliderInner>
                    <Slider min={0} max={ColorGroups.length - 1} value={group} onChange={onBrightness} />
                </SliderInner>
                <SliderIcon>
                    <Icon size="1" src="deqo://icons/hero-mini/moon" />
                </SliderIcon>
            </SliderFrame>
        }
    </Frame>;
});

const removeHash = (x?: string | null) => !!x && x.startsWith('#') ? x.slice(1) : x;
